import type { DevSettingDto } from '@/api';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { useNotification } from '@/hooks/use-notification';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { getEnvironmentLabel } from '@/utils/get-environment';
import {
  Button,
  Table,
  Tag,
  TextField,
  Tooltip,
} from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useRef, useState } from 'react';
import {
  LOCKED_SETTING_MESSAGE,
  getBooleanSettingValue,
  isSettingLocked,
  isValidJson,
} from '../dev-dashboard-helpers';
import { useUpdateDevSetting } from '../dev-dashboard-queries';
import { CompactTable } from './compact-table';
import { SettingChangeConfirmModal } from './setting-change-confirm-modal';

type Props = {
  settings: DevSettingDto[];
};

type Draft = { key: string; value: string };

// Same height and text size as the compact Edit button of the table.
const compactTagStyle = {
  minHeight: 'var(--ds-layout-size-300)',
  height: 'var(--ds-layout-size-300)',
  paddingBlock: 0,
  fontSize: 'var(--ds-screen-text-font-size-body-xs)',
  // The tag text has --ds-layout-spacing-100 of padding on each side. Moving the tag left by the same
  // amount puts the text of true/false on the same line as the other values of the column.
  marginInlineStart: 'calc(-1 * var(--ds-layout-spacing-100))',
} as const;

/** Booleans show as a green (true) or blue (false) tag, like the status tags of the Overview tab. */
const SettingValue = ({ value }: { value: string }) => {
  const booleanValue = getBooleanSettingValue(value);
  if (booleanValue === undefined) return <code>{value}</code>;
  return (
    <Tag
      size="small"
      variant={booleanValue ? 'positive' : 'informative'}
      aria-label={String(booleanValue)}
      style={compactTagStyle}
    >
      {String(booleanValue)}
    </Tag>
  );
};

// Connect small buttons are 32px high. This makes them fit a compact table row.
const compactButtonStyle = {
  minHeight: 'var(--ds-layout-size-300)',
  paddingBlock: 0,
  fontSize: 'var(--ds-screen-text-font-size-body-xs)',
} as const;

// Connect cells do not wrap. Values can be long JSON (for example e-mail lists), so the value
// column wraps at any character. The table then fits its card and scrolls only vertically.
const wrappingCellStyle = {
  textAlign: 'left',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
} as const;

/**
 * All rows of the settings table. A row can be edited; the value is JSON text.
 * The edited value is kept while the list reloads.
 */
export const SettingsTable = ({ settings }: Props) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const showNotification = useNotification();
  const showErrorNotification = useErrorNotification();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);
  const environment = getEnvironmentLabel();

  const { mutate: updateSetting, isPending } = useUpdateDevSetting(
    () => {
      showNotification({ type: 'success', title: 'Setting saved' });
      setIsConfirmOpen(false);
      setDraft(null);
    },
    (error) => {
      setIsConfirmOpen(false);
      showErrorNotification(error, 'save the setting');
    },
  );

  if (settings.length === 0) {
    return <Text>No settings.</Text>;
  }

  const isDraftValid = draft !== null && isValidJson(draft.value);

  return (
    <>
      <CompactTable ariaLabel="Settings">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Key</Table.HeaderCell>
            <Table.HeaderCell style={{ textAlign: 'left' }}>
              Value
            </Table.HeaderCell>
            <Table.HeaderCell>Updated At</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {settings.map((setting) => {
            const isEditing = draft?.key === setting.key;
            return (
              <Table.Row key={setting.key}>
                <Table.Cell>{setting.key}</Table.Cell>
                <Table.Cell style={wrappingCellStyle}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      aria-label={`Value of ${setting.key}`}
                      value={draft.value}
                      validationState={isDraftValid ? undefined : 'invalid'}
                      onChange={(_event, value) =>
                        setDraft({ key: setting.key, value })
                      }
                    />
                  ) : (
                    <SettingValue value={setting.value} />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {setting.updatedAt
                    ? formatDateTimeDisplay(setting.updatedAt)
                    : '-'}
                </Table.Cell>
                <Table.Cell>
                  {isEditing ? (
                    <Flex gap={2}>
                      <Button
                        ref={saveButtonRef}
                        size="small"
                        style={compactButtonStyle}
                        variant="primary"
                        loading={isPending}
                        disabled={!isDraftValid}
                        onClick={() => setIsConfirmOpen(true)}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        style={compactButtonStyle}
                        variant="neutral"
                        disabled={isPending}
                        onClick={() => setDraft(null)}
                      >
                        Cancel
                      </Button>
                    </Flex>
                  ) : isSettingLocked(setting.key) ? (
                    // A disabled button gets no hover events, so the tooltip trigger wraps it.
                    <Tooltip>
                      <Tooltip.Trigger>
                        <span style={{ display: 'inline-flex' }}>
                          <Button
                            size="small"
                            style={compactButtonStyle}
                            variant="neutral"
                            disabled
                            aria-label={`Edit ${setting.key}: ${LOCKED_SETTING_MESSAGE}`}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip.Trigger>
                      <Tooltip.Content>
                        {LOCKED_SETTING_MESSAGE}
                      </Tooltip.Content>
                    </Tooltip>
                  ) : (
                    <Button
                      size="small"
                      style={compactButtonStyle}
                      variant="neutral"
                      disabled={isPending}
                      onClick={() =>
                        setDraft({ key: setting.key, value: setting.value })
                      }
                    >
                      Edit
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </CompactTable>
      {draft && (
        <SettingChangeConfirmModal
          isOpen={isConfirmOpen}
          triggerRef={saveButtonRef}
          environment={environment}
          settingKey={draft.key}
          newValue={draft.value}
          isSaving={isPending}
          onConfirm={() => updateSetting(draft)}
          onClose={() => setIsConfirmOpen(false)}
        />
      )}
    </>
  );
};
